// frontend/src/features/ogdb2/components/MassLOEBasisModal.jsx
//
// Bulk LOE Basis editor for OGDB2 Bulk Analysis.
//
// Lists every unit currently visible in the analysis grid (filteredSortedRows)
// with its Lease Number (assessor), Lease Name (assessor), and current LOE
// Basis (assessor). User checks the rows they want to change, picks a target
// basis (gross / net_of_marketing), and clicks Apply.
//
// On apply, only the assessor's loe_basis fact is written — the resolver
// already mirrors actual & BA basis to whatever the assessor has.

import React, { useEffect, useMemo, useState } from "react";

const BASIS_OPTIONS = [
  { value: "gross",            label: "Gross" },
  { value: "net_of_marketing", label: "Net of Marketing" },
];

function basisLabel(v) {
  const s = String(v || "").trim().toLowerCase();
  if (s === "gross") return "Gross";
  if (s === "net_of_marketing" || s === "net" || s === "net of marketing") return "Net of Marketing";
  return s ? s : "—";
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v !== null && v !== undefined && String(v).trim() !== "") return v;
  }
  return null;
}

export default function MassLOEBasisModal({
  open,
  onClose,
  rows = [],
  getCell,
  onApply,            // async ({ unitIds, newBasis }) => void
}) {
  const [target, setTarget] = useState("gross");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(null); // { done, total }
  const [filterText, setFilterText] = useState("");

  // Build the row list from props.
  const items = useMemo(() => {
    if (typeof getCell !== "function") return [];
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((r) => {
        const unitId = Number(r?.unit?.id);
        if (!Number.isFinite(unitId)) return null;
        const leaseNumber = firstNonEmpty(
          getCell(r, "m.lease_number.assessor"),
          r?.unit?.lease_number,
        );
        const leaseName = firstNonEmpty(
          getCell(r, "m.lease_name.assessor"),
          r?.unit?.lease_name,
          r?.unit?.well_name,
          r?.unit?.display_name,
        );
        const currentBasis = firstNonEmpty(
          getCell(r, "m.loe_basis.assessor"),
        );
        return {
          unitId,
          leaseNumber: leaseNumber ?? "—",
          leaseName: leaseName ?? "—",
          currentBasis,
        };
      })
      .filter(Boolean);
  }, [rows, getCell]);

  // Reset selection / errors each time the modal opens
  useEffect(() => {
    if (!open) return;
    setSelected(new Set(items.map((x) => x.unitId)));  // start with all selected
    setErr("");
    setBusy(false);
    setProgress(null);
    setFilterText("");
  }, [open]); // intentionally only on open toggle

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (x) =>
        String(x.leaseNumber).toLowerCase().includes(q) ||
        String(x.leaseName).toLowerCase().includes(q) ||
        String(x.currentBasis || "").toLowerCase().includes(q)
    );
  }, [items, filterText]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((x) => selected.has(x.unitId));

  if (!open) return null;

  function toggleOne(unitId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  }
  function checkAllFiltered() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const x of filtered) next.add(x.unitId);
      return next;
    });
  }
  function uncheckAllFiltered() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const x of filtered) next.delete(x.unitId);
      return next;
    });
  }

  async function handleApply() {
    setErr("");
    const unitIds = Array.from(selected);
    if (!unitIds.length) {
      setErr("Select at least one row to apply.");
      return;
    }
    if (!target) {
      setErr("Pick a target basis.");
      return;
    }
    setBusy(true);
    setProgress({ done: 0, total: unitIds.length });
    try {
      await onApply?.({
        unitIds,
        newBasis: target,
        onProgress: ({ done, total }) => setProgress({ done, total }),
      });
      onClose?.();
    } catch (e) {
      setErr(String(e?.message || e || "Failed to apply basis change."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl flex flex-col w-[820px] max-w-[95vw] h-[80vh] max-h-[820px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Mass LOE Basis
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Set Assessor LOE Basis across multiple leases. Actual and BA basis
              mirror the assessor automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center text-xl leading-none disabled:opacity-40"
          >
            &times;
          </button>
        </header>

        {/* Toolbar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-200 bg-slate-50/60">
          <label className="text-xs font-semibold text-slate-700">
            Change all to:
          </label>
          <select
            className="text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={busy}
          >
            {BASIS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <span className="mx-2 h-5 w-px bg-slate-300" />

          <button
            type="button"
            className="text-xs font-medium text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline disabled:opacity-40"
            onClick={checkAllFiltered}
            disabled={busy || filtered.length === 0}
          >
            Check all{filterText ? " (filtered)" : ""}
          </button>
          <button
            type="button"
            className="text-xs font-medium text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline disabled:opacity-40"
            onClick={uncheckAllFiltered}
            disabled={busy || filtered.length === 0}
          >
            Uncheck all{filterText ? " (filtered)" : ""}
          </button>

          <div className="ml-auto relative">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter…"
              disabled={busy}
              className="text-sm border border-slate-300 rounded-md pl-8 pr-3 py-1.5 w-56 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 disabled:opacity-50"
            />
            <svg className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M16 10.5a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z" />
            </svg>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="text-sm text-slate-500 p-6 text-center">
              No rows visible in the analysis grid. Adjust your search/filters and reopen.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-100 text-slate-700 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left w-10">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={() => allFilteredSelected ? uncheckAllFiltered() : checkAllFiltered()}
                      disabled={busy || filtered.length === 0}
                      aria-label="Toggle all"
                    />
                  </th>
                  <th className="px-4 py-2 text-left">Lease Number (A)</th>
                  <th className="px-4 py-2 text-left">Lease Name (A)</th>
                  <th className="px-4 py-2 text-left">Current LOE Basis (A)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((x) => {
                  const isSel = selected.has(x.unitId);
                  return (
                    <tr
                      key={x.unitId}
                      className={`cursor-pointer ${isSel ? "bg-emerald-50/40" : "hover:bg-slate-50"}`}
                      onClick={() => !busy && toggleOne(x.unitId)}
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleOne(x.unitId)}
                          disabled={busy}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-700">
                        {x.leaseNumber}
                      </td>
                      <td className="px-4 py-2 text-slate-800">
                        {x.leaseName}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          String(x.currentBasis || "").toLowerCase() === "gross"
                            ? "bg-blue-50 text-blue-700 border border-blue-200"
                            : String(x.currentBasis || "").toLowerCase().startsWith("net")
                              ? "bg-amber-50 text-amber-800 border border-amber-200"
                              : "bg-slate-100 text-slate-500 border border-slate-200"
                        }`}>
                          {basisLabel(x.currentBasis)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between px-6 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-600">
            {selected.size} of {items.length} selected
            {filterText ? ` · ${filtered.length} match filter` : ""}
            {progress ? ` · ${progress.done} of ${progress.total} applied` : ""}
            {err ? <span className="ml-2 text-red-600 font-medium">{err}</span> : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-1.5 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={busy || selected.size === 0}
              className="px-4 py-1.5 rounded-md bg-[#0E1B3D] text-white text-sm font-medium hover:bg-[#1F3164] disabled:opacity-50"
            >
              {busy ? "Applying…" : `Apply to ${selected.size}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
